import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BookingWidget } from "../components/BookingWidget";
import { Gallery } from "../components/Gallery";
import { LocationMap } from "../components/LocationMap";
import { Testimonials } from "../components/Testimonials";
import { PartnerRegistration } from "../components/PartnerRegistration";
import { NewsSection } from "../components/NewsSection";
import Swal from "sweetalert2";

import {
  MapPin,
  Clock,
  ChevronRight,
  QrCode,
  Sparkles,
  Waves,
  Zap,
  Box,
  Rabbit,
  Ticket,
} from "lucide-react";
import { MobileActionState } from "../types";

const HERO_IMAGES = [
  "https://ik.imagekit.io/publish/bg1.png",
  "https://ik.imagekit.io/publish/bg2.png",
  "https://ik.imagekit.io/publish/bg3.png",
];

interface HomeProps {
  setIsCheckBookingOpen?: (open: boolean) => void;
}

export const Home: React.FC<HomeProps> = ({ setIsCheckBookingOpen }) => {
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [currentHeroIdx, setCurrentHeroIdx] = useState(0);
  const [activeCategory, setActiveCategory] = useState<
    "personal" | "rombongan" | null
  >(null);
  const navigate = useNavigate();

  // Slideshow logic for Desktop Hero
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroIdx((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Helper function for smooth scrolling with header offset
  const smoothScrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Default Action State (Scroll to Booking)
  const defaultAction: MobileActionState = {
    label: "Beli Tiket Sekarang",
    onClick: () => smoothScrollTo("book-section"),
    disabled: false,
    priority: 0,
  };

  const [mobileAction, setMobileAction] =
    useState<MobileActionState>(defaultAction);

  const updateMobileAction = useCallback((action: MobileActionState | null) => {
    if (action) {
      setMobileAction((prev) =>
        action.priority >= (prev.priority || 0) ? action : prev
      );
    } else {
      setMobileAction(defaultAction);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyNav(window.scrollY > 100);
      // Reset active category on scroll
      if (activeCategory) setActiveCategory(null);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeCategory]);

  useEffect(() => {
    // Check for hash in URL to scroll to section
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace("#", "");
      if (id === "book") {
        setTimeout(() => smoothScrollTo("book-section"), 500);
      } else {
        setTimeout(() => smoothScrollTo(id), 500);
      }
    }
  }, []);

  return (
    <div className="flex flex-col overflow-x-hidden">
      <header
        id="hero"
        className="relative min-h-[90vh] md:min-h-screen pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden flex items-center bg-slate-950"
      >
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="hidden lg:block absolute inset-0">
            {HERO_IMAGES.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`Zoo Hero ${idx + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-2000 ease-in-out ${
                  currentHeroIdx === idx
                    ? "opacity-40 scale-105"
                    : "opacity-0 scale-100"
                }`}
                loading="lazy"
              />
            ))}
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/60 to-transparent hidden lg:block"></div>
          <div className="absolute inset-0 bg-slate-950 lg:hidden opacity-100"></div>
          <div className="absolute top-1/4 -left-1/4 w-[200%] h-[100%] bg-indigo-600/10 rounded-full blur-[150px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 mb-16 lg:mb-0 text-center lg:text-left">
              <div className="inline-flex items-center gap-3 py-2 px-5 rounded-full bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl mb-8 animate-fadeIn">
                <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
                <span className="text-white font-black text-[10px] md:text-[11px] uppercase tracking-[0.25em]">
                  Tiket Resmi GIZ & RIW Surabaya
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tighter leading-[1.1] mb-8 md:mb-10 uppercase drop-shadow-2xl">
                Magical <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600">
                  Digital
                </span>{" "}
                & <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600">
                  Rabbit Village
                </span>
              </h1>
              <p className="text-base md:text-xl text-slate-300 mb-12 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
                Satu-satunya tiket resmi untuk <strong>GIZ Park</strong> dan{" "}
                <strong>Rabbit In Wonderland</strong>. Hemat waktu dengan{" "}
                <strong>E-Ticket QR</strong> resmi kami.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-4 bg-white/5 backdrop-blur-lg px-6 py-4 rounded-3xl shadow-2xl border border-white/10 hover:bg-white/10 transition-all cursor-default group">
                  <MapPin className="w-5 h-5 text-emerald-400 group-hover:scale-125 transition-transform" />
                  <div className="text-left">
                    <p className="text-[9px] font-black text-white/40 uppercase tracking-widest leading-none mb-1.5">
                      Lokasi Utama
                    </p>
                    <p className="text-xs font-bold text-white uppercase tracking-tight">
                      KBS Surabaya Area Selatan
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/5 backdrop-blur-lg px-6 py-4 rounded-3xl shadow-2xl border border-white/10 hover:bg-white/10 transition-all cursor-default group">
                  <Clock className="w-5 h-5 text-indigo-400 group-hover:scale-125 transition-transform" />
                  <div className="text-left">
                    <p className="text-[9px] font-black text-white/40 uppercase tracking-widest mb-1.5 leading-none">
                      Waktu Operasional
                    </p>
                    <p className="text-xs font-bold text-white uppercase tracking-tight">
                      08:00 - 16:00 WIB Setiap Hari
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="book-section"
              className="lg:col-span-5 relative z-20 animate-slideUp"
            >
              <div className="absolute -inset-10 bg-indigo-500/10 rounded-[4rem] blur-[100px] opacity-30"></div>
              <BookingWidget
                setMobileAction={updateMobileAction}
                activeCategory={activeCategory}
                onActivate={() => setActiveCategory("personal")}
                onSwitchCategory={(cat) => setActiveCategory(cat)}
              />
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" className="flex-grow">
        <section
          id="experience"
          className="py-24 md:py-32 bg-white"
          aria-labelledby="exp-title"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20 md:mb-28">
              <span
                id="exp-title"
                className="text-indigo-600 font-black uppercase tracking-[0.5em] text-[10px] md:text-[12px]"
              >
                The Ultimate Adventure
              </span>
              <h2 className="text-3xl md:text-5xl font-black text-slate-950 mt-5 tracking-tighter uppercase leading-tight">
                Explore the Wonders
              </h2>
            </div>

            {/* RIW Section */}
            <div className="mb-28 md:mb-40">
              <div className="flex items-center justify-between mb-10 md:mb-16 border-l-[12px] border-riw-primary pl-8">
                <div>
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-950 uppercase tracking-tighter">
                    Rabbit In Wonderland
                  </h3>
                  <p className="text-slate-500 font-bold text-[10px] md:text-xs uppercase tracking-[0.4em] mt-2">
                    Interaksi Langsung Penuh Keajaiban
                  </p>
                </div>
                <div className="hidden md:flex gap-3">
                  <div className="p-4 bg-orange-50 rounded-3xl">
                    <Rabbit className="w-10 h-10 text-riw-primary" />
                  </div>
                </div>
              </div>

              <div className="flex md:grid md:grid-cols-3 gap-6 md:gap-10 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-2 md:px-0 mb-12">
                {[
                  {
                    title: "Rabbit Castle",
                    desc: "Istana megah bergaya Eropa dengan koloni kelinci langka yang siap menyambut.",
                    icon: "🏰",
                    img: "https://ik.imagekit.io/publish/RIW/4.PNG",
                    longDesc:
                      "Jelajahi istana megah bergaya arsitektur klasik Eropa yang dihuni oleh puluhan jenis kelinci unik dan langka dari seluruh penjuru dunia.",
                  },
                  {
                    title: "Giant Chess & Snow",
                    desc: "Nikmati sensasi gelembung salju terbesar dan catur raksasa di tengah keramaian kelinci.",
                    icon: "❄️",
                    img: "https://ik.imagekit.io/publish/RIW/9.PNG",
                    longDesc:
                      "Bermain petak umpet di antara papan catur raksasa sementara butiran salju buatan (magic bubble) turun menyelimuti taman bermain.",
                  },
                  {
                    title: "Bukit Teletubbies",
                    desc: "Area interaksi terbuka yang lembut di hamparan bukit hijau dengan ratusan kelinci bebas berkeliaran.",
                    icon: "🌿",
                    img: "https://ik.imagekit.io/publish/RIW/5.PNG",
                    longDesc:
                      "Hamparan bukit hijau nan lembut tempat ratusan kelinci bebas berlarian. Anda bisa memberi makan dan berfoto sepuasnya di sini.",
                  },
                ].map((zone, i) => (
                  <article
                    key={i}
                    onClick={() =>
                      Swal.fire({
                        title: `<span class="font-black uppercase tracking-tight text-xl text-orange-500">${zone.title}</span>`,
                        html: `<p class="text-sm text-slate-600 font-medium leading-relaxed p-4">${zone.longDesc}</p>`,
                        confirmButtonText: "MENARIK!",
                        confirmButtonColor: "#F97316",
                        customClass: {
                          popup: "rounded-[2.5rem]",
                          confirmButton:
                            "rounded-2xl px-8 py-3 font-black text-[10px] uppercase tracking-widest",
                        },
                      })
                    }
                    className="group relative rounded-[2.5rem] md:rounded-[3.5rem] overflow-hidden shadow-2xl aspect-[4/5] bg-slate-950 flex-none w-[300px] md:w-auto snap-center cursor-pointer"
                  >
                    <img
                      src={zone.img}
                      width="800"
                      height="1000"
                      alt={zone.title}
                      className="w-full h-full object-cover opacity-60 transition-all duration-1000 group-hover:scale-110 group-hover:opacity-80"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/10 to-transparent flex flex-col justify-end p-8 md:p-12">
                      <div className="absolute top-8 right-8 bg-white/10 backdrop-blur-md border border-white/20 p-3 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0 shadow-2xl">
                        <ChevronRight className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-4xl md:text-6xl mb-6 transform group-hover:-translate-y-2 transition-transform duration-700">
                        {zone.icon}
                      </div>
                      <h4 className="text-white text-xl md:text-2xl font-black uppercase tracking-tighter mb-3 leading-tight">
                        {zone.title}
                      </h4>
                      <p className="text-slate-300 text-xs md:text-sm font-medium leading-relaxed opacity-90">
                        {zone.desc}
                      </p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
                {[
                  {
                    title: "Public Space",
                    desc: "Event & Aktivitas.",
                    icon: "🎪",
                    longDesc:
                      "Area terbuka untuk berbagai event hiburan dan aktivitas interaktif pengunjung.",
                  },
                  {
                    title: "Meja Belajar",
                    desc: "Pinjam & Belajar.",
                    icon: "🎒",
                    longDesc:
                      "Fasilitas peminjaman perlengkapan belajar untuk anak-anak di area Wonderland.",
                  },
                  {
                    title: "Playground",
                    desc: "Area Bermain.",
                    icon: "🎡",
                    longDesc:
                      "Taman bermain ramah anak dengan standar keamanan internasional.",
                  },
                  {
                    title: "Gratis Sepeda",
                    desc: "Min. 2 Cup Wortel.",
                    icon: "🚲",
                    longDesc:
                      "Dapatkan kesempatan berkeliling dengan sepeda gratis untuk setiap pembelian 2 cup wortel.",
                  },
                  {
                    title: "Pistol Bubble",
                    desc: "Gratis Pinjam.",
                    icon: "🫧",
                    longDesc:
                      "Tersedia pistol bubble gratis untuk menambah keseruan berfoto bersama kelinci.",
                  },
                  {
                    title: "Wonder Café",
                    desc: "Area Kuliner.",
                    icon: "🥤",
                    longDesc:
                      "Nikmati camilan dan minuman segar dengan pemandangan langsung ke Bukit Teletubbies.",
                  },
                ].map((feat, i) => (
                  <div
                    key={i}
                    onClick={() =>
                      Swal.fire({
                        title: `<span class="font-black uppercase tracking-tight text-xl">${feat.title}</span>`,
                        html: `<p class="text-sm text-slate-600 font-medium leading-relaxed p-4">${feat.longDesc}</p>`,
                        iconHtml: `<span class="text-4xl">${feat.icon}</span>`,
                        confirmButtonText: "KEREN!",
                        confirmButtonColor: "#F97316",
                        customClass: {
                          popup: "rounded-[2.5rem]",
                          confirmButton:
                            "rounded-2xl px-8 py-3 font-black text-[10px] uppercase tracking-widest",
                        },
                      })
                    }
                    className="bg-orange-50/40 border border-orange-100 p-6 rounded-[2.5rem] hover:bg-white hover:shadow-2xl hover:-translate-y-2 transition-all group text-center cursor-pointer"
                  >
                    <div className="text-3xl mb-4 group-hover:scale-125 transition-transform duration-700">
                      {feat.icon}
                    </div>
                    <h5 className="font-black text-slate-900 text-[10px] uppercase tracking-widest mb-1">
                      {feat.title}
                    </h5>
                    <p className="text-[9px] text-slate-400 font-bold uppercase leading-tight tracking-tighter">
                      {feat.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* GIZ Section */}
            <div>
              <div className="flex items-center justify-between mb-10 md:mb-16 border-l-[12px] border-giz-primary pl-8">
                <div>
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-slate-950 uppercase tracking-tighter">
                    Glow Interactive Zoo
                  </h3>
                  <p className="text-slate-500 font-bold text-[10px] md:text-xs uppercase tracking-[0.4em] mt-2">
                    Pusat Wisata Edukasi Digital Tercanggih
                  </p>
                </div>
                <div className="hidden md:flex gap-3">
                  <div className="p-4 bg-indigo-50 rounded-3xl">
                    <Zap className="w-10 h-10 text-giz-primary" />
                  </div>
                </div>
              </div>

              <div className="flex md:grid md:grid-cols-3 gap-6 md:gap-10 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-2 md:px-0">
                {[
                  {
                    title: "Animasi 3D",
                    desc: "Hewan digital interaktif yang hidup di dinding dan lantai.",
                    icon: Waves,
                    img: "https://ik.imagekit.io/publish/GIZ/1c.jpg",
                    longDesc:
                      "Hewan-hewan digital 3D yang dapat berinteraksi dengan pengunjung melalui teknologi augmented reality, menciptakan pengalaman edukasi yang imersif dan menyenangkan.",
                  },
                  {
                    title: "Rumah Balon",
                    desc: "Ribuan bola bercahaya yang berubah warna saat berinteraksi fisik.",
                    icon: Box,
                    img: "https://ik.imagekit.io/publish/GIZ/1d.jpg",
                    longDesc:
                      "Ribuan bola bercahaya (LED Ball) yang saling terhubung dan berubah warna ketika disentuh, memberikan pengalaman sensorik yang magis.",
                  },
                  {
                    title: "Hutan Menyala",
                    desc: "Area hutan dengan pepohonan dan tanaman yang bersinar di kegelapan.",
                    icon: Zap,
                    img: "https://ik.imagekit.io/publish/GIZ/1b.jpg",
                    longDesc:
                      "Area hutan buatan dengan pepohonan dan tanaman yang dilengkapi dengan lampu LED, menciptakan suasana magis di malam hari.",
                  },
                ].map((zone, i) => (
                  <article
                    key={i}
                    onClick={() =>
                      Swal.fire({
                        title: `<span class="font-black uppercase tracking-tight text-xl text-indigo-600">${zone.title}</span>`,
                        html: `<p class="text-sm text-slate-600 font-medium leading-relaxed p-4">${zone.longDesc}</p>`,
                        confirmButtonText: "KEREN!",
                        confirmButtonColor: "#4F46E5",
                        customClass: {
                          popup: "rounded-[2.5rem]",
                          confirmButton:
                            "rounded-2xl px-8 py-3 font-black text-[10px] uppercase tracking-widest",
                        },
                      })
                    }
                    className="group relative rounded-[2.5rem] md:rounded-[3.5rem] overflow-hidden shadow-2xl aspect-[4/5] bg-slate-950 flex-none w-[300px] md:w-auto snap-center cursor-pointer"
                  >
                    <img
                      src={zone.img}
                      width="800"
                      height="1000"
                      alt={zone.title}
                      className="w-full h-full object-cover opacity-60 transition-all duration-1000 group-hover:scale-110 group-hover:opacity-80"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/10 to-transparent flex flex-col justify-end p-8 md:p-12">
                      <div className="absolute top-8 right-8 bg-indigo-500/20 backdrop-blur-md border border-indigo-400/30 p-3 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0 shadow-2xl">
                        <ChevronRight className="w-5 h-5 text-white" />
                      </div>
                      <div className="bg-giz-primary p-3 md:p-4 rounded-2xl w-fit mb-6 shadow-2xl group-hover:scale-110 transition-transform">
                        <zone.icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                      </div>
                      <h4 className="text-white text-xl md:text-2xl font-black uppercase tracking-tighter mb-3 leading-tight">
                        {zone.title}
                      </h4>
                      <p className="text-slate-300 text-xs md:text-sm font-medium leading-relaxed opacity-90">
                        {zone.desc}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <PartnerRegistration
          setMobileAction={updateMobileAction}
          activeCategory={activeCategory}
          onActivate={() => setActiveCategory("rombongan")}
        />
        <NewsSection />
        <Gallery />
        <Testimonials />
        <LocationMap />
      </main>

      {/* Sticky Mobile Nav Action */}
      <div
        className={`fixed bottom-0 left-0 w-full p-3 pb-5 z-[90] md:hidden transition-all duration-700 ${
          showStickyNav
            ? "translate-y-0 opacity-100"
            : "translate-y-24 opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-white/95 backdrop-blur-2xl border border-white/40 rounded-2xl p-2 shadow-[0_15px_40px_-10px_rgba(0,0,0,0.25)] flex gap-2 max-w-sm mx-auto overflow-hidden">
          <button
            onClick={() => setIsCheckBookingOpen?.(true)}
            className="w-11 h-11 bg-slate-100 rounded-xl text-slate-700 flex flex-col items-center justify-center gap-0.5 active:scale-90 transition-all flex-shrink-0"
          >
            <QrCode className="w-4 h-4 text-indigo-600" />
            <span className="text-[7px] font-black uppercase tracking-tighter">
              Status
            </span>
          </button>
          <button
            onClick={mobileAction.onClick}
            disabled={mobileAction.disabled}
            className="flex-1 h-11 rounded-xl font-black text-white text-[10px] uppercase tracking-[0.15em] shadow-lg flex items-center justify-center gap-1.5 transition-all active:scale-[0.97] disabled:opacity-50 bg-giz-primary"
          >
            <span className="animate-fadeIn" key={mobileAction.label}>
              {mobileAction.label}
            </span>
            {!mobileAction.disabled && <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
};
